import { SearchableSelect } from '@st/theme'
import type { PaymentMessage, selectPaymentFields, TaxPayment } from './model'
const inputClass = 'w-full rounded-md border-gray-300 bg-white px-3 py-2 text-sm'

function PaymentField({
  label,
  value,
  onChange,
  type = 'text'
}: {
  label: string
  value: string
  onChange: (value: string) => void
  type?: string
}) {
  return (
    <label className="flex flex-col gap-2 text-sm text-gray-700">
      <span>{label}</span>
      <input
        className={inputClass}
        type={type}
        step="any"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  )
}

export function TaxPaymentFields({
  model,
  send,
  dueOn,
  setDueOn
}: {
  model: ReturnType<typeof selectPaymentFields>
  send: (message: PaymentMessage) => void
  dueOn: string
  setDueOn: (value: string) => void
}) {
  const { payment } = model
  return (
    <section className="space-y-5">
      <label className="flex flex-col gap-2 text-sm text-gray-700">
        Jurisdiction
        <select
          className={inputClass}
          value={payment.jurisdiction.level}
          onChange={(event) => send({ type: 'jurisdictionChanged', level: event.target.value })}
        >
          <option value="federal">Federal</option>
          <option value="state">State</option>
          <option value="local">Local</option>
        </select>
      </label>
      {payment.jurisdiction.level == 'state' && (
        <label className="flex flex-col gap-2 text-sm text-gray-700">
          State
          <SearchableSelect
            title="State"
            placeholder="Select state"
            emptyLabel="No states found"
            value={payment.jurisdiction.code ?? ''}
            options={model.states}
            buildValue={(entry) => entry.identifier}
            buildLabel={(entry) => entry.name}
            onChange={(identifier) => {
              if (identifier) send({ type: 'stateChanged', identifier })
            }}
          />
        </label>
      )}
      {payment.jurisdiction.level == 'local' && (
        <PaymentField
          label="Local jurisdiction"
          value={payment.jurisdiction.name}
          onChange={(name) => send({ type: 'localNameChanged', name })}
        />
      )}
      <label className="flex flex-col gap-2 text-sm text-gray-700">
        Payment for
        <select
          className={inputClass}
          value={payment.paymentPurpose}
          onChange={(event) =>
            send({
              type: 'purposeChanged',
              purpose: event.target.value as TaxPayment['paymentPurpose']
            })
          }
        >
          <option value="balance_due">Balance due</option>
          <option value="estimated_tax">Estimated tax</option>
        </select>
      </label>
      {payment.paymentPurpose == 'estimated_tax' && (
        <PaymentField
          label="Installment"
          value={model.installment}
          type="number"
          onChange={(value) => send({ type: 'installmentChanged', value })}
        />
      )}
      <div className="grid grid-cols-2 gap-4">
        <PaymentField
          label="Amount ($)"
          value={String(payment.amount)}
          type="number"
          onChange={(value) => send({ type: 'amountChanged', value })}
        />
        <PaymentField label="Due date" value={dueOn} type="date" onChange={setDueOn} />
      </div>
      <label className="flex flex-col gap-2 text-sm text-gray-700">
        Method
        <select
          className={inputClass}
          value={payment.method}
          onChange={(event) =>
            send({ type: 'methodChanged', method: event.target.value as TaxPayment['method'] })
          }
        >
          <option value="online">Online</option>
          <option value="mail">Mail</option>
          <option value="automatic_withdrawal">Automatic withdrawal</option>
        </select>
      </label>
      {payment.method == 'automatic_withdrawal' && (
        <div className="grid grid-cols-2 gap-4">
          <PaymentField
            label="Bank name"
            value={model.bankName}
            onChange={(value) => send({ type: 'bankNameChanged', value })}
          />
          <PaymentField
            label="Account last four"
            value={model.last4}
            onChange={(value) => send({ type: 'accountLastFourChanged', value })}
          />
        </div>
      )}
    </section>
  )
}
